import { useMemo } from 'react';
import { marketEvents } from '@/mocks/marketCalendar';
import { dayNumber, monthShort, weekdayFull, weekdayLabel } from '../calculations';
import { CurrencyFlag } from './ui';

const WINDOW = [-1, 0, 1, 2, 3, 4, 5];
const OUTLOOK = [0, 1, 2, 3, 4, 5, 6];

export default function DateNavigator({
  dayOffset,
  setDayOffset,
}: {
  dayOffset: number;
  setDayOffset: (v: number) => void;
}) {
  const min = Math.min(...WINDOW);
  const max = Math.max(...WINDOW);

  const outlook = useMemo(() => {
    return OUTLOOK.map((off) => {
      const evs = marketEvents.filter((e) => e.dayOffset === off);
      const high = evs.filter((e) => e.impact === 'high').length;
      const freq = new Map<string, number>();
      for (const e of evs) freq.set(e.currency, (freq.get(e.currency) ?? 0) + 1);
      let main = '';
      let m = 0;
      for (const [cur, c] of freq) if (c > m) { m = c; main = cur; }
      return { off, total: evs.length, high, main };
    });
  }, []);

  const shift = (dir: number) => setDayOffset(Math.min(max, Math.max(min, dayOffset + dir)));

  return (
    <section className="rounded-3xl border border-slate-200/80 bg-white p-6 md:p-8">
      {/* Horizontal date nav */}
      <div className="flex items-center justify-between gap-3">
        <button
          type="button"
          onClick={() => shift(-1)}
          className="flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 text-slate-500 transition-colors hover:border-violet-300 hover:text-violet-600"
        >
          <i className="ri-arrow-left-s-line text-lg" />
        </button>

        <div className="flex flex-1 flex-wrap items-center justify-center gap-1.5">
          {WINDOW.map((off) => {
            const active = off === dayOffset;
            return (
              <button
                key={off}
                type="button"
                onClick={() => setDayOffset(off)}
                className={`flex min-w-[52px] flex-col items-center rounded-xl px-2 py-1.5 transition-all ${
                  active ? 'bg-violet-600 text-white' : 'text-slate-600 hover:bg-slate-50'
                }`}
              >
                <span className={`text-[10px] font-semibold uppercase ${active ? 'text-violet-200' : 'text-slate-400'}`}>
                  {off === 0 ? 'Hôm nay' : weekdayLabel(off)}
                </span>
                <span className="font-mono text-sm font-bold tabular-nums">{dayNumber(off)}</span>
                <span className={`text-[9px] ${active ? 'text-violet-200' : 'text-slate-400'}`}>{monthShort(off)}</span>
              </button>
            );
          })}
        </div>

        <button
          type="button"
          onClick={() => shift(1)}
          className="flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 text-slate-500 transition-colors hover:border-violet-300 hover:text-violet-600"
        >
          <i className="ri-arrow-right-s-line text-lg" />
        </button>
      </div>

      <button
        type="button"
        onClick={() => setDayOffset(0)}
        className={`mx-auto mt-3 block whitespace-nowrap rounded-full px-4 py-1.5 text-xs font-semibold transition-colors ${
          dayOffset === 0 ? 'bg-violet-50 text-violet-700' : 'text-violet-600 hover:bg-violet-50'
        }`}
      >
        Hôm nay
      </button>

      {/* 7-day outlook */}
      <div className="mt-6 border-t border-slate-200/80 pt-6">
        <h3 className="text-sm font-bold text-slate-800">Lịch thị trường 7 ngày</h3>
        <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-7">
          {outlook.map((d) => {
            const active = d.off === dayOffset;
            return (
              <button
                key={d.off}
                type="button"
                onClick={() => setDayOffset(d.off)}
                className={`rounded-2xl border p-3 text-center transition-all ${
                  active ? 'border-violet-600 bg-violet-50' : 'border-slate-200/80 bg-white hover:border-violet-200'
                }`}
              >
                <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">{weekdayFull(d.off)}</p>
                <p className={`mt-1 font-mono text-xl font-bold tabular-nums ${active ? 'text-violet-700' : 'text-slate-800'}`}>
                  {d.total}
                </p>
                <p className="text-[10px] text-slate-400">{d.total} sự kiện</p>
                {d.high > 0 && (
                  <p className="mt-1 text-[10px] font-semibold text-rose-500">{d.high} tác động cao</p>
                )}
                {d.main && (
                  <p className="mt-1 flex items-center justify-center gap-1 text-[11px] font-semibold text-slate-600">
                    <CurrencyFlag code={d.main} />
                    {d.main}
                  </p>
                )}
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
}